import React, { Component } from 'react'
import 'array-flat-polyfill'
import 'whatwg-fetch'
import 'promise-polyfill/src/polyfill'
import Store from './Store'
import Fuse from 'fuse.js'

import Page from './result/Page'
import News from './result/News'
import Button from './result/Button'
import People from './result/People'

import ModalResults from './results/ModalResults'
import InlineResults from './results/InlineResults'

import ButtonPortal from './ButtonPortal'
import ButtonTrigger from './ButtonTrigger'

const ResultComponents = {
  news: News,
  people: People,
  events: Page,
  button: Button,
}

const GroupTitles = {
  externalSearchResults: 'External Sites',
}

const GroupOrder = [
  'events',
  'pages',
  'news',
  'courses',
  'people',
  'curatedSearchResults',
  'externalSearchResults',
]

const fuseOptions = {
  shouldSort: true,
  threshold: 0.2,
  location: 0,
  distance: 1000,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: [
    "title",
    "synonyms",
    "excerpt",
  ]
}

const MAX_RESULTS_PER_GROUP = 5

export default class ResultsContainer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      results: [],
      resultsOrdered: [],
      groups: [],
      fuse: null,
      searchTerm: '',
      selected: null,
      loading: false,
    }

    this.searchInput = React.createRef()
    this.focusable = React.createRef()

    this.handleChange = this.handleChange.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.selectResult = this.selectResult.bind(this)
    this.sortResults = this.sortResults.bind(this)
  }

  componentDidMount() {
    this.setState({
      loading: true
    })

    Store.getSearchIndex(this.props.searchBaseUrl, this.props.searchPath).then((searchIndex) => {
      let fuse = new Fuse(searchIndex.data, {
        shouldSort: true,
        threshold: 0.2,
        location: 0,
        distance: 1000,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
          "title",
          "synonyms",
          "excerpt",
          "body",
          "accordion",
          "html",
        ]
      })
      this.setState({
        fuse: fuse,
        results: fuse.search(this.state.searchTerm),
        loading: false
      }, function() {
        this.sortResults(this.state.searchTerm)
      })
    })
  }

  componentDidUpdate() {
    if (this.focusable && this.focusable.current) {
      this.focusable.current.focus()
    }
  }

  handleKeydown(event) {
    if ((event.target.tagName !== "INPUT" && event.target.tagName !== "TEXTAREA") ||
         event.target === this.searchInput.current) {

      if (!event.ctrlKey && !event.altKey && !event.metaKey) {
        if (!this.props.active) {
          // a-z, A-Z
          if (event.keyCode >= 65 && event.keyCode <= 90) {
            this.setState({ searchTerm: '', results: [], groups: [] })
            this.props.handleOpen()
          }
        }
        else {
          if (event.key === 'Escape') {
            this.props.handleClose()
            return
          }
          else if (event.key === 'ArrowUp') {
            const currentIndex = this.state.resultsOrdered.indexOf(this.state.selected)
            const nextIndex = Math.max(currentIndex - 1, 0)
            this.setState({ selected: this.state.resultsOrdered[nextIndex] })
            event.preventDefault()
          }
          else if (event.key === 'ArrowDown') {
            const currentIndex = this.state.resultsOrdered.indexOf(this.state.selected)
            const nextIndex = Math.min(currentIndex + 1, this.state.resultsOrdered.length - 1)
            this.setState({ selected: this.state.resultsOrdered[nextIndex] })
            event.preventDefault()
          }
          else if (event.key === 'Enter') {
            if (this.state.selected.type === "externalSearchResults") {
              window.open(this.state.selected.url, '_blank')
            }
            else {
              window.location = this.state.selected.url
            }
          }
          else if (event.key === 'Backspace') {
            if (this.props.inline && this.state.searchTerm.length === 1) {
              this.props.handleClose()
            }
          }
          else {
            this.searchInput.current.focus()
          }
        }
      }
      else if (this.searchInput.current) {
        this.searchInput.current.focus()
      }
    }

    if (this.props.inline && this.searchInput.current) {
      this.searchInput.current.focus()
    }
  }

  selectResult(result) {
    this.setState({ selected: result }, () => {
      if (this.props.inline && this.searchInput.current) {
        this.searchInput.current.focus()
      }
    })
  }

  handleChange(event) {
    this.sortResults(event.target.value)
  }

  sortResults(term) {
    let results = []
    let groups = []
    let resultsOrdered = []
    let curatedResultsGroup = []

    if (this.state.fuse && term !== '') {
      results = this.state.fuse.search(term)
      
      // If there's an exact keyword match, only show related results for that keyword:
      let curatedSearchResultMatch = results.find((result) => result.type === 'curatedSearchResults' && (result.title.toLowerCase() === term.toLowerCase() || result.synonyms.find((synonym) => synonym.toLowerCase() === term.toLowerCase())) )
      
      // Remove curated search results from the results array
      results = results.filter((result) => result.type !== 'curatedSearchResults')
      
      if (curatedSearchResultMatch) {
        // remove duplicates from results
        results = results.filter((result) => curatedSearchResultMatch.results.indexOf(result.id) < 0 )

        curatedResultsGroup = curatedSearchResultMatch.results.map((resultId) => {
          return window.ecuadStore['searchindex'].data.find((item) => { return item.id === resultId })
        })
        curatedResultsGroup = curatedResultsGroup.filter((item) => { return typeof item !== "undefined" })
      }
      
      const unsortedGroups = results.reduce((groups, result) => {
        const index = groups.findIndex((group) => group.type === result.type)
        if (index > -1) {
          if (groups[index].results.length < MAX_RESULTS_PER_GROUP) {
            groups[index].results.push(result)
          }
        }
        else {
          groups.push({
            type: result.type,
            results: [result]
          });
        }
        return groups
      }, [])

      groups = GroupOrder.map(group => {
        const match = unsortedGroups.find(g => g.type === group)
        return typeof match === "undefined" ? { type: group, results: [] } : match
      });

      // prepend the curatedSearchResults group to groups and the results
      if (curatedResultsGroup.length > 0) {
        groups.unshift({
          type: 'Featured',
          results: curatedResultsGroup
        })
        results.unshift(...curatedResultsGroup)
      }

      // append the Search Full Site result
      if (term) {
        groups.push({
          type: 'button',
          results: [{
            type: 'button',
            id: 'search',
            url: `/search?q=${term}`,
            title: 'Search Full Site'
          }]
        })
      }

      resultsOrdered = groups.map((group) => group.results).flat()
    }
    this.setState({
      searchTerm: term,
      results: results,
      groups: groups,
      resultsOrdered: resultsOrdered,
      selected: results[0],
    }, () => {
      if (this.searchInput.current && this.state.searchTerm.length > 0) { this.searchInput.current.focus() }
    })
  }

  render() {
    const numResults = this.state.resultsOrdered.length ? this.state.resultsOrdered.length - 1 : 0
    
    const props = { 
      active: this.props.active,
      focusable: this.focusable, 
      groups: this.state.groups, 
      handleChange: this.handleChange,
      searchInput: this.searchInput,
      searchTerm: this.searchTerm, 
      selected: this.state.selected, 
      selectResult: this.selectResult, 
      handleKeydown: this.handleKeydown,
      handleOpen: this.props.handleOpen,
      loading: this.state.loading,
      numResults: numResults,
      handleClose: this.props.handleClose,
    }

    return this.props.inline ? <InlineResults {...props} /> : (
      <>
      <ModalResults {...props} />  
      <ButtonPortal>
        <ButtonTrigger onClick={this.props.handleOpen} active={this.props.active} />
      </ButtonPortal>
      </>
    )   
  }
}
